import { requestByPOST } from 'lib/requests'
import { buildSearchMapPropertiesPayload } from './mappers'
import type { PriceType } from 'src/types/prices'

const OPTIONS = {
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
  withCredentials: true,
}

const GEO_END_POINTS = {
  SEARCH_MAP: '/v3/search-by-coordinates-geo',
  SEARCH_MAP_AUTH: '/v3/search-by-coordinates-geo/member',
}

export interface FetchMapPropertiesParams {
  adults: number[]
  affiliate: string
  checkIn: string
  checkOut: string
  children: number[][]
  city: string
  citySlug: string
  country: string
  countryCode: string
  currency: string
  description: string
  filters: {
    accommodationTypes: string[]
    facilities: string[]
    family: 'true' | 'false'
    maxPrice: number
    minPrice: number
    rating?: string
    stars: string[]
    themes: string[]
    boards: string[]
    paymentOptions: string[]
    cancellationPolicies: string[]
  }
  highlightedProperty: number
  id: string
  ignoreProperty: boolean
  internal_navigation: string
  language: string
  lastSuggestion: {
    latitude: number
    longitude: number
    language: string
    city: string
    country: string
  }
  latitude: number
  longitude: number
  market: string
  offset: number
  radius: undefined
  region: string
  searchTerm: string
  slug: string
  source: string
  term: string
  text: string
  type: string
  zone: string
  firstSearchDone: boolean
  isLogged: boolean
  device?: string
  minSellingRate: number
  excludedIDs?: number[]
  keepProperties: boolean
  boxShape: {
    topLeft: { latitude: number; longitude: number }
    bottomRight: { latitude: number; longitude: number }
  }
  priceType: PriceType
}

interface Property {
  accommodation_type: string
  address: string
  avg_rate: number
  bighead_rate: number
  comments: number
  currency: string
  distance: { unit: string; value: number }
  distance_to_center: { unit: string; value: number }
  facilities: { code_group: string; id: string }[]
  highlighted: boolean
  id: number
  images: { bucket: string; code: string; height: number; path: string }[]
  is_outside_city: boolean
  locations: {
    coordinates: { lat: number; lon: number }
    id_location: string
    location_descriptor: string
    name: string
    name_slug: string
    type: string
  }[]
  name: string
  nights: number
  property_location: {
    city: string
    city_slug: string
    coordinates: { lat: number; lon: number }
    country: string
    country_code: string
    id_location: string
  }
  rating: number
  rooms: {
    avg_rate: number
    bighead_rate: number
    board_code: string
    board_name: string
    catalogue: string
    credential_source: string
    is_free_cancellation: boolean
    is_prime: boolean
    provider_name: string
    rate_class: string
    rate_discounts: { mobile: boolean; prime: boolean }
    room_code: string
    room_name: string
    selling_rate: number
    subtotal: number
    summary: {
      additional_fees: {
        amount: number
        currency: string
        local: { amount: number; currency: string }
      }
      subtotal: { amount: number; currency: string }
      subtotal_nightly: { amount: number; currency: string }
      subtotal_nightly_with_fees: { amount: number; currency: string }
      subtotal_with_fees: { amount: number; currency: string }
      taxes: { amount: number; currency: string }
      total: { amount: number; currency: string }
      total_nightly: { amount: number; currency: string }
      total_nightly_with_fees: { amount: number; currency: string }
      total_with_fees: { amount: number; currency: string }
    }
  }
}

export interface FetchMapPropertiesResponse {
  data: {
    metadata: {
      count: number
      location_corners: {
        bottom_right: {
          lat: number
          lon: number
        }
        top_left: {
          lat: number
          lon: number
        }
      }
      price: {
        distribution: {
          count: number
          min: number
        }[]
        max: number
        min: number
      }
    }
    properties: Property[]
  }
  status: 200
}

export interface FetchMapPropertiesError {
  data: {
    errorCode: string
  }
  status: 400
}

export type FetchMapPropertiesResult = FetchMapPropertiesResponse | FetchMapPropertiesError

export function fetchMapProperties(
  params: FetchMapPropertiesParams
): Promise<FetchMapPropertiesResult> {
  const endpoint = GEO_END_POINTS.SEARCH_MAP
  return requestByPOST({
    data: buildSearchMapPropertiesPayload(params),
    endpoint,
  })
}

export function fetchMapPropertiesAuthorized(
  params: FetchMapPropertiesParams
): Promise<FetchMapPropertiesResult> {
  const endpoint = GEO_END_POINTS.SEARCH_MAP_AUTH

  return requestByPOST({
    data: buildSearchMapPropertiesPayload({ ...params, source: 'web' }),
    endpoint,
    options: OPTIONS,
  })
}
